import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { useClientNameQuery } from "@/query/clientQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import { BookingTable } from "@okeys/shared-utils"
import { CalendarIcon, PersonIcon, UpdateIcon } from "@radix-ui/react-icons"
import {
  Calendar,
  Clock,
  Euro,
  Mail,
  Scissors,
  User,
  UserCheck,
} from "lucide-react"

interface BookingDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  reservation: BookingTable | null
}

export const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  isOpen,
  onClose,
  reservation,
}) => {
  const { client, isLoading: isLoadingClient } = useClientNameQuery(
    reservation?.clientId || ""
  )
  const { services } = useServicesQuery()

  if (!reservation) return null

  const isPastReservation = (startTime: string) => {
    return new Date(startTime) < new Date()
  }

  const getStatusLabel = (state: BookingTable["state"], startTime: string) => {
    if (state === "pending") {
      return isPastReservation(startTime)
        ? "En attente de validation"
        : "À venir"
    }
    switch (state) {
      case "validated":
        return "Validé"
      case "cancelled":
        return "Annulé"
      case "absent":
        return "Absent"
      default:
        return state
    }
  }

  const getStatusColor = (
    state: BookingTable["state"],
    startTime: string
  ): string => {
    if (state === "pending") {
      return isPastReservation(startTime)
        ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
        : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
    }
    switch (state) {
      case "validated":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
      case "absent":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300"
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      fullDate: date.toLocaleString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    }
  }

  const getPersonTypeLabel = (type: string) => {
    switch (type) {
      case "client":
        return "Client"
      case "prestatere":
        return "Prestataire"
      case "manager":
        return "Manager"
      case "admin":
        return "Administrateur"
      default:
        return type
    }
  }

  const startIso = ((reservation as any).startTime || reservation.scheduledStart) as string
  const endIso = ((reservation as any).endTime || reservation.scheduledEnd) as string
  const startDateTime = formatDateTime(startIso)
  const endDateTime = formatDateTime(endIso)
  const lastUpdateDateTime = formatDateTime(reservation.lastUpdateAt)

  const servicesWithKeys = reservation.services.map((service) =>
    services?.find((s) => s.id === service.serviceId)
  )
  const servicesWithoutKeys = reservation.services.map((service) =>
    services?.find((s) => s.id === service.serviceId)
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
        {/* Header fixe */}
        <DialogHeader className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
          <DialogTitle className="text-2xl font-bold">
            Détails du rendez-vous
          </DialogTitle>
        </DialogHeader>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Statut avec design amélioré */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Statut
                </span>
                <Badge
                  className={`${getStatusColor(reservation.state, startIso)} px-3 py-1`}
                >
                  {getStatusLabel(reservation.state, startIso)}
                </Badge>
              </div>
            </div>

            {/* Informations client avec icônes améliorées */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <PersonIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                Informations client
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <UserCheck className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Nom
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {isLoadingClient ? (
                        <Skeleton className="h-5 w-32" />
                      ) : (
                        client?.name || "Non renseigné"
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Email
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100 break-all">
                      {isLoadingClient ? (
                        <Skeleton className="h-5 w-48" />
                      ) : (
                        client?.email || "Non renseigné"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations du rendez-vous avec nouveau design */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                Informations du rendez-vous
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Date
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {startDateTime.date}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Horaire
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {startDateTime.time} - {endDateTime.time}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Prestataire
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      Prestataire #{reservation.prestatereId.slice(0, 8)}...
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Euro className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Total
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-lg">
                      {((reservation as any).total || (reservation as any).price || 0) as number}€
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Prestataire et services avec design amélioré */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Scissors className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {servicesWithKeys?.map((service) => (
                  <div key={service?.id}>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      - {service?.name} ({service?.fidelityPoints} Keys)
                    </p>
                  </div>
                ))}
                {servicesWithoutKeys?.map((service) => (
                  <div key={service?.id}>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      - {service?.name} ({service?.price}€)
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className=" rounded-lg justify-center items-center p-2 border-t border-gray-200 dark:border-gray-700">
          {/* Informations de mise à jour */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg w-full flex justify-center items-center">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <UpdateIcon className="h-4 w-4" />
              <span>
                Dernière mise à jour le {lastUpdateDateTime.fullDate} par{" "}
                <span className="font-medium">
                  {getPersonTypeLabel(reservation.lastUpdateByType)}
                </span>
              </span>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
